/**
 * Variance display utility — caps absurd attendance variance values.
 *
 * Background
 * ----------
 * When a clock-in / clock-out event happens days off from the scheduled
 * shift window (e.g. wrong-day clock-out, midnight rollover bug,
 * accidental tap on a stale calendar entry), the backend variance math
 * produces nonsensical large values like ``early_leave_minutes = 4060``
 * (≈ 67 hours). Showing "Left early by 4060m" to an approver is
 * misleading — the timesheet needs human triage, not an exception
 * indicator that looks routine.
 *
 * Rule
 * ----
 * If |variance| > LARGE_VARIANCE_THRESHOLD_MIN (4 hours), suppress the
 * raw number and render "Large variance — review required" instead.
 *
 * Helpers
 * -------
 * • formatLateBadge(min)      → "Late by 12m"  | "Large variance — review required"
 * • formatEarlyLeaveBadge(min) → "Left early by 7m" | "Large variance — review required"
 * • formatOvertimeBadge(min)  → "Overtime: 22m" | "Large variance — review required"
 * • formatLargeVarianceTitle(min) → tooltip text shown over the badge
 * • isLargeVariance(min)      → boolean predicate
 *
 * All helpers tolerate null / undefined / non-numeric input → return
 * empty string so the caller can decide whether to render anything.
 */

export const LARGE_VARIANCE_THRESHOLD_MIN = 240; // ±4 hours
export const LARGE_VARIANCE_LABEL = 'Large variance — review required';

const _isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export function isLargeVariance(minutes) {
  return _isNum(minutes) && Math.abs(minutes) > LARGE_VARIANCE_THRESHOLD_MIN;
}

/**
 * Formats a minute count as either "Xh Ym" / "Ym" or the large-variance
 * sentinel string. Returns '' for null / NaN.
 */
export function formatVarianceMinutes(minutes) {
  if (!_isNum(minutes)) return '';
  if (isLargeVariance(minutes)) return LARGE_VARIANCE_LABEL;
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const sign = minutes < 0 ? '-' : '';
  if (h > 0) return `${sign}${h}h ${m}m`;
  return `${sign}${m}m`;
}

export function formatLateBadge(minutes) {
  if (!_isNum(minutes) || minutes <= 0) return '';
  if (isLargeVariance(minutes)) return LARGE_VARIANCE_LABEL;
  return `Late by ${minutes}m`;
}

export function formatEarlyLeaveBadge(minutes) {
  if (!_isNum(minutes) || minutes <= 0) return '';
  if (isLargeVariance(minutes)) return LARGE_VARIANCE_LABEL;
  return `Left early by ${minutes}m`;
}

export function formatOvertimeBadge(minutes) {
  if (!_isNum(minutes) || minutes <= 0) return '';
  if (isLargeVariance(minutes)) return LARGE_VARIANCE_LABEL;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `Overtime: ${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `Overtime: ${minutes}m`;
}

/**
 * Tooltip / title attribute text. When the variance is large, the
 * tooltip exposes the raw underlying number alongside the sentinel so
 * approvers who hover have access to the exact figure for triage.
 */
export function formatVarianceTitle(label, minutes) {
  if (!_isNum(minutes)) return label || '';
  if (isLargeVariance(minutes)) {
    return `${LARGE_VARIANCE_LABEL} (raw: ${minutes}m)`;
  }
  return `${label} ${minutes}m`;
}
